/**
 * Card displaying client consultations with scheduling and tracking
 */

import { useState } from 'react';
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Plus,
  Video,
  ExternalLink,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { format, formatDistanceToNow, isPast, isToday } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useClientConsultations,
  useConsultationFrequency,
  useConsultationMutations,
  Consultation,
} from '@/hooks/use-consultations';
import { ScheduleConsultationDialog } from './ScheduleConsultationDialog';
import { CompleteConsultationDialog } from './CompleteConsultationDialog';
import { cn } from '@/lib/utils';

interface ClientConsultationsCardProps {
  clientId: string;
  clientName: string;
}

export function ClientConsultationsCard({
  clientId,
  clientName,
}: ClientConsultationsCardProps) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);

  const { data: consultations = [], isLoading } = useClientConsultations(clientId);
  const { data: frequency } = useConsultationFrequency(clientId);
  const { cancelConsultation, deleteConsultation } = useConsultationMutations();

  // Separate scheduled and past consultations
  const scheduledConsultations = consultations.filter(c => c.status === 'scheduled');
  const pastConsultations = consultations.filter(c => c.status !== 'scheduled');

  const getStatusBadge = (consultation: Consultation) => {
    switch (consultation.status) {
      case 'scheduled':
        const scheduledDate = consultation.scheduled_at ? new Date(consultation.scheduled_at) : null;
        if (scheduledDate && isPast(scheduledDate) && !isToday(scheduledDate)) {
          return <Badge variant="destructive">Overdue</Badge>;
        }
        if (scheduledDate && isToday(scheduledDate)) {
          return <Badge className="bg-info">Today</Badge>;
        }
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'completed':
        return <Badge className="bg-success">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline">Cancelled</Badge>;
      case 'no_show':
        return <Badge variant="destructive">No Show</Badge>;
      default:
        return null;
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      initial: 'Initial',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      renewal: 'Renewal',
      adhoc: 'Ad-hoc',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Consultations
            </CardTitle>
            {frequency && (
              <p className="text-sm text-muted-foreground mt-1">
                {frequency.completed_consultations} completed
                {frequency.avg_days_between > 0 && (
                  <> &bull; ~{Math.round(frequency.avg_days_between)} days between</>
                )}
              </p>
            )}
          </div>
          <Button onClick={() => setShowSchedule(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Schedule
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Upcoming/Scheduled */}
          {scheduledConsultations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">Upcoming</h4>
              <div className="space-y-3">
                {scheduledConsultations.map((consultation) => (
                  <ConsultationItem
                    key={consultation.id}
                    consultation={consultation}
                    getStatusBadge={getStatusBadge}
                    getTypeLabel={getTypeLabel}
                    onComplete={() => setSelectedConsultation(consultation)}
                    onCancel={() => cancelConsultation.mutate(consultation.id)}
                    onDelete={() => deleteConsultation.mutate(consultation.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Consultations */}
          {pastConsultations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">Past</h4>
              <div className="space-y-3">
                {pastConsultations.slice(0, 5).map((consultation) => (
                  <ConsultationItem
                    key={consultation.id}
                    consultation={consultation}
                    getStatusBadge={getStatusBadge}
                    getTypeLabel={getTypeLabel}
                    isPast
                    onDelete={() => deleteConsultation.mutate(consultation.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {consultations.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No consultations scheduled</p>
              <p className="text-sm mt-1">
                Schedule regular check-ins to stay connected with {clientName}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <ScheduleConsultationDialog
        open={showSchedule}
        onOpenChange={setShowSchedule}
        clientId={clientId}
        clientName={clientName}
      />

      {selectedConsultation && (
        <CompleteConsultationDialog
          open={!!selectedConsultation}
          onOpenChange={(open) => !open && setSelectedConsultation(null)}
          consultation={selectedConsultation}
        />
      )}
    </>
  );
}

// Individual consultation item
function ConsultationItem({
  consultation,
  getStatusBadge,
  getTypeLabel,
  isPast,
  onComplete,
  onCancel,
  onDelete,
}: {
  consultation: Consultation;
  getStatusBadge: (c: Consultation) => React.ReactNode;
  getTypeLabel: (type: string) => string;
  isPast?: boolean;
  onComplete?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
}) {
  const scheduledAt = consultation.scheduled_at ? new Date(consultation.scheduled_at) : null;

  return (
    <div
      className={cn(
        "p-3 rounded-lg border",
        isPast ? "bg-muted/30" : "bg-background"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">
              {consultation.title || getTypeLabel(consultation.consultation_type)}
            </span>
            {getStatusBadge(consultation)}
            <Badge variant="outline" className="text-xs">
              {getTypeLabel(consultation.consultation_type)}
            </Badge>
          </div>

          {scheduledAt && (
            <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {format(scheduledAt, 'MMM d, yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {format(scheduledAt, 'h:mm a')}
              </span>
              {consultation.duration_minutes && (
                <span>({consultation.duration_minutes} min)</span>
              )}
            </div>
          )}

          {consultation.notes && isPast && (
            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {consultation.notes}
            </p>
          )}

          {consultation.outcome_summary && (
            <p className="text-sm mt-2 p-2 bg-success/10 rounded border border-success/15">
              {consultation.outcome_summary}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          {consultation.meeting_link && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              asChild
            >
              <a href={consultation.meeting_link} target="_blank" rel="noopener noreferrer">
                <Video className="h-4 w-4" />
              </a>
            </Button>
          )}

          {!isPast && consultation.status === 'scheduled' && onComplete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-success hover:text-success hover:bg-success/10"
              onClick={onComplete}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Complete
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isPast && consultation.status === 'scheduled' && onCancel && (
                <DropdownMenuItem onClick={onCancel}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
